import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold leading-normal transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-muted disabled:text-muted-foreground disabled:border-border disabled:shadow-none disabled:bg-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-soft hover:bg-primary-dark hover:shadow-medium hover:scale-105 active:scale-95",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border-2 border-primary bg-transparent text-primary hover:bg-primary hover:text-primary-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-soft hover:bg-secondary-dark hover:shadow-medium hover:scale-105 active:scale-95",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-hero text-primary-foreground shadow-medium hover:shadow-soft hover:scale-110 active:scale-95 text-base font-bold",
        success: "bg-success text-success-foreground shadow-soft hover:shadow-medium hover:scale-105 active:scale-95",
        accent: "bg-accent text-accent-foreground shadow-soft hover:bg-accent-dark hover:shadow-medium hover:scale-105 active:scale-95",
        premium: "bg-gradient-cta text-primary-foreground shadow-medium hover:shadow-soft hover:scale-110 active:scale-95 font-bold",
        warning: "bg-warning text-warning-foreground shadow-soft hover:shadow-medium hover:scale-105 active:scale-95",
        // Child-friendly variant with larger touch target
        child: "bg-primary text-primary-foreground hover:bg-primary-dark shadow-medium hover:shadow-soft hover:scale-110 active:scale-95 font-bold min-h-[56px] min-w-[56px] text-lg rounded-2xl",
      },
      size: {
        default: "h-11 px-6 py-3",
        sm: "h-9 rounded-md px-4 py-2 text-xs",
        lg: "h-14 rounded-xl px-10 py-4 text-base",
        xl: "h-16 rounded-2xl px-12 py-5 text-lg", // Extra large for kids
        icon: "h-11 w-11",
        "icon-sm": "h-8 w-8", // Compact icon button (AI Elements)
        "icon-lg": "h-14 w-14", // Larger icon button for kids

      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
